import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  Avatar,
  Tab,
  Tabs,
  MenuItem,
  Divider,
  useTheme,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Menu
} from "@mui/material";
import { styled, keyframes, alpha } from "@mui/system";
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import ArticleIcon from '@mui/icons-material/Article';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import DescriptionIcon from '@mui/icons-material/Description';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
  AreaChart,
  Area
} from 'recharts';
import { getDashboardData } from "../../services/userService";
import { toast } from "react-toastify";
import { useNavigate } from 'react-router-dom';
import { getAllOffers } from "../../services/offerService";
import { getLenderById } from "../../services/userService";
import { getAllLoanApplications } from "../../services/loanService";

const COLORS = {
  primary: {
    glass: 'rgba(99, 102, 241, 0.1)',
    lighter: '#EEF2FF',
    light: '#818CF8',
    main: '#6366F1',
    dark: '#4F46E5',
    gradient: 'linear-gradient(135deg, #6366F1 0%, #818CF8 100%)',
    glassmorphism: 'rgba(99, 102, 241, 0.08)',
    modernGradient: 'linear-gradient(135deg, #6366F1 0%, #A5B4FC 50%, #818CF8 100%)'
  },
  success: {
    glass: 'rgba(34, 197, 94, 0.1)',
    lighter: '#DCFCE7',
    light: '#86EFAC',
    main: '#22C55E',
    dark: '#16A34A',
    gradient: 'linear-gradient(135deg, #22C55E 0%, #86EFAC 100%)',
    glassmorphism: 'rgba(34, 197, 94, 0.08)',
    modernGradient: 'linear-gradient(135deg, #22C55E 0%, #4ADE80 50%, #86EFAC 100%)'
  },
  warning: {
    glass: 'rgba(245, 158, 11, 0.1)',
    lighter: '#FEF3C7',
    light: '#FCD34D',
    main: '#F59E0B',
    dark: '#D97706',
    gradient: 'linear-gradient(135deg, #F59E0B 0%, #FCD34D 100%)',
    glassmorphism: 'rgba(245, 158, 11, 0.08)',
    modernGradient: 'linear-gradient(135deg, #F59E0B 0%, #FBBF24 50%, #FCD34D 100%)'
  },
  error: {
    glass: 'rgba(239, 68, 68, 0.1)',
    lighter: '#FEE2E2',
    light: '#FCA5A5',
    main: '#EF4444',
    dark: '#DC2626',
    gradient: 'linear-gradient(135deg, #EF4444 0%, #FCA5A5 100%)',
    glassmorphism: 'rgba(239, 68, 68, 0.08)',
    modernGradient: 'linear-gradient(135deg, #EF4444 0%, #F87171 50%, #FCA5A5 100%)'
  },
  purple: {
    glass: 'rgba(168, 85, 247, 0.1)',
    lighter: '#F3E8FF',
    light: '#C084FC',
    main: '#A855F7',
    dark: '#9333EA',
    gradient: 'linear-gradient(135deg, #A855F7 0%, #C084FC 100%)',
    glassmorphism: 'rgba(168, 85, 247, 0.08)',
    modernGradient: 'linear-gradient(135deg, #A855F7 0%, #B469F9 50%, #C084FC 100%)'
  }
};

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.4);
  }
  70% {
    transform: scale(1.05);
    box-shadow: 0 0 0 10px rgba(99, 102, 241, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(99, 102, 241, 0);
  }
`;

const GlassCard = styled(Card)(({ theme, gradient }) => ({
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(20px)',
  borderRadius: 24,
  border: '1px solid rgba(255, 255, 255, 0.3)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  animation: `${fadeIn} 0.6s ease-out`,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: gradient,
  },
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.12)',
    '& .icon-container': {
      animation: `${pulse} 1.5s infinite`
    }
  }
}));

const StyledChip = styled(Chip)(({ theme, color }) => ({
  borderRadius: 8,
  height: 28,
  padding: '0 8px',
  fontWeight: 600,
  fontSize: '0.75rem',
  backgroundColor: COLORS[color].glass,
  color: COLORS[color].main,
  border: `1px solid ${COLORS[color].light}`,
  '& .MuiChip-label': {
    padding: '0 8px'
  }
}));

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          padding: '16px',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
        }}
      >
        <Typography variant="subtitle2" sx={{ color: '#1E293B', mb: 1, fontWeight: 600 }}>
          {label}
        </Typography>
        {payload.map((entry, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: entry.color,
              }}
            />
            <Typography variant="body2" sx={{ color: '#64748B' }}>
              {entry.name}: <span style={{ color: '#1E293B', fontWeight: 600 }}>{entry.value}</span>
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }
  return null;
};

const StatisticBox = ({ label, value, trend, percentage, color }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
    <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 500 }}>
      {label}
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2 }}>
      <Typography variant="h3" sx={{
        fontWeight: 700,
        color: '#1E293B',
        background: COLORS[color || 'primary'].gradient,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent'
      }}>
        {value}
      </Typography>
      {trend && percentage && (
        <StyledChip
          icon={trend === 'up' ? <TrendingUpIcon sx={{ fontSize: 16 }} /> : <TrendingDownIcon sx={{ fontSize: 16 }} />}
          label={`${percentage}%`}
          color={trend === 'up' ? 'success' : 'error'}
        />
      )}
    </Box>
  </Box>
);

const DashboardContainer = styled(Box)(({ theme }) => ({
  padding: { xs: '24px', md: '40px' },
  background: 'linear-gradient(135deg, #F8FAFC 0%, #EEF2FF 100%)',
  minHeight: '100vh',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 50% 50%, rgba(99, 102, 241, 0.05) 0%, rgba(0, 0, 0, 0) 50%)',
    pointerEvents: 'none'
  },
  '& .MuiGrid-container': {
    marginTop: 0,
    marginBottom: 0
  }
}));

const DashboardHeader = styled(Box)(({ theme }) => ({
  marginBottom: '3rem',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-1.5rem',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '80%',
    height: '1px',
    background: 'linear-gradient(90deg, transparent 0%, rgba(99, 102, 241, 0.3) 50%, transparent 100%)'
  },
  marginTop: '0.5rem',
}));

const ModernButton = styled(Button)(({ color = 'primary' }) => ({
  borderRadius: '12px',
  padding: '12px 24px',
  background: COLORS[color].modernGradient,
  color: 'white',
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: `0 8px 16px ${alpha(COLORS[color].main, 0.2)}`,
  transition: 'all 0.3s ease',
  border: `1px solid ${alpha(COLORS[color].main, 0.1)}`,
  backdropFilter: 'blur(10px)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 12px 20px ${alpha(COLORS[color].main, 0.3)}`,
    background: COLORS[color].modernGradient,
    filter: 'brightness(1.1)'
  }
}));

const Dashboard = () => {
  const [userName, setUserName] = useState("User");
  const [selectedChart, setSelectedChart] = useState(0);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offers, setOffers] = useState([]);
  const [recentLoans, setRecentLoans] = useState([]);
  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    const user = localStorage.getItem("user");
    if (user) {
      try {
        const userData = JSON.parse(user);
        if (userData.username) {
          setUserName(userData.username);
        }
      } catch (error) {
        toast.error("Error parsing user data");
      }
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
    fetchOffers();
    fetchRecentLoans();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await getDashboardData();
      if (response.responseCode === 200) {
        setDashboardData(response.result);
      }
    } catch (error) {
      toast.error("Error fetching dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const fetchOffers = async () => {
    try {
      const response = await getAllOffers();
      if (response.statusCode === 200 && response.result?.result) {
        const latestOffers = response.result.result
          .slice(0, 3);

        const offersWithDetails = await Promise.all(
          latestOffers.map(async (offerData) => {
            try {
              const offer = offerData.offer || offerData;

              const facility = offer?.facilities?.[0];
              const toNumber = (value) => {
                const n = Number(value);
                return Number.isFinite(n) ? n : 0;
              };
              const interestRate = toNumber(facility?.rateOfIntrest);
              const tenureYears = Math.round(toNumber(facility?.repaymentTenure) / 12);
              const amountLakhs = Math.round(toNumber(facility?.loanOffered) / 100000);
                            
              if (!offer.lenderId) {
                return {
                  id: offer.id,
                  bankName: "Bank Name",
                  bankLogo: "",
                  interestRate,
                  tenure: tenureYears,
                  amount: amountLakhs,
                  status: offer.offerStatus
                };
              }
              
              const lenderResponse = await getLenderById(offer.lenderId);
              const lenderDetails = lenderResponse.result.lender;

              return {
                id: offer.id,
                bankName: lenderDetails?.lender_financialInstitution || "Bank Name",
                bankLogo: lenderDetails?.profilePhotoUrl || "",
                interestRate,
                tenure: tenureYears,
                amount: amountLakhs,
                status: offer.offerStatus
              };
            } catch (error) {
              const offer = offerData.offer || offerData;
              toast.error(`Error fetching lender details for ID ${offer.lenderId}:`, error);
              return {
                id: offer.id,
                bankName: "Bank Name",
                bankLogo: "",
                interestRate: 0,
                tenure: 0,
                amount: 0,
                status: offer.offerStatus
              };
            }
          })
        );

        setOffers(offersWithDetails);
      }
    } catch (error) {
      toast.error("Error fetching offers:", error);
    }
  };

  const fetchRecentLoans = async () => {
    try {
      const response = await getAllLoanApplications();
      if (response.responseCode === 200 && response.result?.data) {
        const latestLoans = response.result.data
          .slice(0, 3)
          .map(loan => ({
            id: loan.applicationId,
            applicationNumber: loan.applicationNumber,
            borrowerName: loan.userId.name,
            borrowerType: loan.borrowerType,
            amount: loan.fundRequirement === '5_10L' ? '5-10' :
              Number(loan.fundRequirement) / 100000,
            status: loan.status,
            timestamp: new Date(loan.createDatetime)
          }));
        setRecentLoans(latestLoans);
      }
    } catch (error) {
      toast.error("Error fetching loans:", error);
    }
  };

  const handleViewAllLoans = () => {
    navigate('/proposals');
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleViewAllOffers = () => {
    navigate('/offers');
  };

  const quickStats = dashboardData ? [
    {
      title: "Leads",
      icon: <PersonIcon />,
      value: dashboardData.totalLeads,
      trend: "up",
      percentage: 8.2,
      color: "primary"
    },
    {
      title: "Users",
      icon: <BusinessIcon />,
      value: (dashboardData.totalUsers || 0) + (dashboardData.totalEmployees || 0),
      trend: "up",
      percentage: 12.5,
      color: "success"
    },
    {
      title: "Proposals",
      icon: <DescriptionIcon />,
      value: dashboardData.totalProposals,
      trend: dashboardData.newProposals > 0 ? "up" : "down",
      percentage: dashboardData.newProposals,
      color: "warning"
    },
    {
      title: "Loans",
      icon: <ArticleIcon />,
      value: dashboardData.totalLoanApplication,
      trend: "down",
      percentage: 3.1,
      color: "purple"
    },
    {
      title: "Offers",
      icon: <LocalOfferIcon />,
      value: dashboardData.totalOffers,
      trend: "up",
      percentage: 5.8,
      color: "error"
    },
    {
      title: "Blogs",
      icon: <ArticleIcon />,
      value: dashboardData.totalBlog,
      trend: dashboardData.totalBlogPublished > dashboardData.NonPublioshedBlogs ? "up" : "down",
      percentage: ((dashboardData.totalBlogPublished / dashboardData.totalBlog) * 100).toFixed(1),
      color: "primary"
    }
  ] : [];

  const usersData = dashboardData ? [
    { name: 'Managers', value: dashboardData.totalRegionalManager + dashboardData.totalAreaManager },
    { name: 'Borrowers', value: dashboardData.totalBorrower },
    { name: 'Lenders', value: dashboardData.totalLender }
  ] : [];

  const loansData = dashboardData ? [
    { name: 'Accepted', value: dashboardData.acceptedLoanApplication },
    { name: 'Submitted', value: dashboardData.submittedApplication },
    { name: 'Rejected', value: dashboardData.rejectedApplication }
  ] : [];

  const trendData = dashboardData ? dashboardData.leadsGraph.map((leadData) => {
    const monthYear = leadData.month;
    const matchingOffer = dashboardData.offerGraph.find(offer => offer.month === monthYear);
    
    return {
      month: new Date(monthYear).toLocaleString('default', { month: 'short' }),
      // Loans Value
      // Loans: 0,
      Offers: matchingOffer ? matchingOffer.totalOffers : 0,
      Leads: leadData.totalleads
    };
  }).sort((a, b) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months.indexOf(a.month) - months.indexOf(b.month);
  }) : [];

  const renderChart = () => {
    switch (selectedChart) {
      case 0:
        return (
          <Box>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={4}>
                <StatisticBox
                  label="Overall Submitted Loan Amount"
                  value={`₹${((dashboardData?.LoanAmount || 0) / 1000000).toFixed(1)}M`}
                  // trend={dashboardData?.revenueGrowth > 0 ? "up" : "down"}
                  // percentage={dashboardData?.revenueGrowth || 0}
                  color="primary"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <StatisticBox
                  label="Active Loans"
                  value={dashboardData?.totalLoanApplication || 0}
                  // trend="up"
                  // percentage={8.2}
                  color="success"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <StatisticBox
                  label="Conversion Rate"
                  value={`${((dashboardData?.acceptedLoanApplication / dashboardData?.totalLoanApplication) * 100).toFixed(1)}%`}
                  // trend={dashboardData?.acceptedLoanApplication > dashboardData?.rejectedApplication ? "up" : "down"}
                  // percentage={((dashboardData?.acceptedLoanApplication / dashboardData?.totalLoanApplication) * 100).toFixed(1)}
                  color="warning"
                />
              </Grid>
            </Grid>
            <ResponsiveContainer width="100%" height={400}>
              <AreaChart data={trendData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                <defs>
                  <linearGradient id="colorLoans" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.primary.main} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={COLORS.primary.main} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorOffers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.success.main} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={COLORS.success.main} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorLeads" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.warning.main} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={COLORS.warning.main} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#E2E8F0"
                  vertical={false}
                />
                <XAxis
                  dataKey="month"
                  stroke="#64748B"
                  tick={{ fill: '#64748B', fontSize: 12, fontWeight: 500 }}
                  axisLine={{ stroke: '#E2E8F0' }}
                  tickLine={{ stroke: '#E2E8F0' }}
                />
                <YAxis
                  stroke="#64748B"
                  tick={{ fill: '#64748B', fontSize: 12, fontWeight: 500 }}
                  axisLine={{ stroke: '#E2E8F0' }}
                  tickLine={{ stroke: '#E2E8F0' }}
                />
                <Tooltip
                  content={<CustomTooltip />}
                  cursor={{ stroke: '#94A3B8', strokeWidth: 1, strokeDasharray: '4 4' }}
                />
                <Legend
                  verticalAlign="top"
                  height={36}
                  iconType="circle"
                  formatter={(value) => (
                    <span style={{ color: '#1E293B', fontSize: '14px', fontWeight: 600 }}>
                      {value}
                    </span>
                  )}
                />
                {/* Loans Value
                <Area
                  type="monotone"
                  dataKey="Loans"
                  stroke={COLORS.primary.main}
                  fill="url(#colorLoans)"
                  strokeWidth={3}
                  dot={{
                    fill: COLORS.primary.main,
                    stroke: 'white',
                    strokeWidth: 2,
                    r: 4
                  }}
                  activeDot={{
                    fill: COLORS.primary.main,
                    stroke: 'white',
                    strokeWidth: 2,
                    r: 6,
                    boxShadow: '0 0 0 4px rgba(99, 102, 241, 0.2)'
                  }}
                />
                */}
                <Area
                  type="monotone"
                  dataKey="Offers"
                  stroke={COLORS.success.main}
                  fill="url(#colorOffers)"
                  strokeWidth={3}
                  dot={{
                    fill: COLORS.success.main,
                    stroke: 'white',
                    strokeWidth: 2,
                    r: 4
                  }}
                  activeDot={{
                    fill: COLORS.success.main,
                    stroke: 'white',
                    strokeWidth: 2,
                    r: 6
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="Leads"
                  stroke={COLORS.warning.main}
                  fill="url(#colorLeads)"
                  strokeWidth={3}
                  dot={{
                    fill: COLORS.warning.main,
                    stroke: 'white',
                    strokeWidth: 2,
                    r: 4
                  }}
                  activeDot={{
                    fill: COLORS.warning.main,
                    stroke: 'white',
                    strokeWidth: 2,
                    r: 6
                  }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        );

      case 1:
        const firstGridData = dashboardData ? [
          {
            name: 'Active Leads',
            value: dashboardData.activeLeads,
            percentage: ((dashboardData.activeLeads / dashboardData.totalLeads) * 100).toFixed(1),
            color: COLORS.success.main,
            gradient: COLORS.success.gradient
          },
          {
            name: 'Eligibility Calculator Leads',
            value: dashboardData.elicalLeads,
            percentage: ((dashboardData.elicalLeads / dashboardData.totalLeads) * 100).toFixed(1),
            color: COLORS.warning.main,
            gradient: COLORS.warning.gradient
          }
        ] : [];

        const secondGridData = dashboardData ? [
          {
            name: 'Advisor Leads',
            value: dashboardData.advisorLeads,
            percentage: ((dashboardData.advisorLeads / dashboardData.totalLeads) * 100).toFixed(1),
            color: COLORS.primary.main,
            gradient: COLORS.primary.gradient
          },
          {
            name: 'Logged In Leads',
            value: dashboardData.loggedInLeads,
            percentage: ((dashboardData.loggedInLeads / dashboardData.totalLeads) * 100).toFixed(1),
            color: COLORS.purple.main,
            gradient: COLORS.purple.gradient
          },
          {
            name: 'Not Eligible Leads',
            value: dashboardData.NotEligibleLeads,
            percentage: ((dashboardData.NotEligibleLeads / dashboardData.totalLeads) * 100).toFixed(1),
            color: COLORS.error.main,
            gradient: COLORS.error.gradient
          }
        ] : [];

        const thirdGridData = dashboardData ? [
          {
            name: 'Under Follow Up Leads',
            value: dashboardData.underFollowUpLeads,
            percentage: ((dashboardData.underFollowUpLeads / dashboardData.totalLeads) * 100).toFixed(1),
            color: COLORS.warning.main,
            gradient: COLORS.warning.gradient
          }
        ] : [];

        return (
          <Box>
            <Box
              sx={{
                p: 3,
                mb: 3,
                bgcolor: 'white',
                borderRadius: 3,
                boxShadow: '0 0 20px rgba(0,0,0,0.05)'
              }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: 2,
                        background: COLORS.primary.gradient,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <PersonIcon sx={{ color: 'white', fontSize: 32 }} />
                    </Box>
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 700,
                          background: COLORS.primary.gradient,
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent'
                        }}
                      >
                        {dashboardData?.totalLeads || 0}
                      </Typography>
                      <Typography variant="subtitle1" sx={{ color: 'text.secondary' }}>
                        Total Leads
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid container spacing={3} sx={{ mt: 4 }}>
                  {firstGridData.map((item) => (
                    <Grid item xs={12} md={4} key={item.name}>
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: alpha(item.color, 0.05),
                          borderRadius: 2,
                          border: `1px solid ${alpha(item.color, 0.1)}`,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 8px 16px ${alpha(item.color, 0.1)}`,
                            bgcolor: alpha(item.color, 0.1)
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '50%',
                              background: item.gradient,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                borderRadius: '50%',
                                bgcolor: 'white',
                                opacity: 0.8
                              }}
                            />
                          </Box>
                          <Box>
                            <Typography variant="h6" sx={{ color: item.color, fontWeight: 700 }}>
                              {item.value}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              {item.name}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Grid>

              <Grid container spacing={3} sx={{ mt: 4 }}>
                {secondGridData.map((item) => (
                  <Grid item xs={12} md={4} key={item.name}>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: alpha(item.color, 0.05),
                        borderRadius: 2,
                        border: `1px solid ${alpha(item.color, 0.1)}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: `0 8px 16px ${alpha(item.color, 0.1)}`,
                          bgcolor: alpha(item.color, 0.1)
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            background: item.gradient,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              bgcolor: 'white',
                              opacity: 0.8
                            }}
                          />
                        </Box>
                        <Box>
                          <Typography variant="h6" sx={{ color: item.color, fontWeight: 700 }}>
                            {item.value}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {item.name}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              <Grid container spacing={3} sx={{ mt: 4 }}>
                {thirdGridData.map((item) => (
                  <Grid item xs={12} md={4} key={item.name}>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: alpha(item.color, 0.05),
                        borderRadius: 2,
                        border: `1px solid ${alpha(item.color, 0.1)}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: `0 8px 16px ${alpha(item.color, 0.1)}`,
                          bgcolor: alpha(item.color, 0.1)
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            background: item.gradient,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              bgcolor: 'white',
                              opacity: 0.8
                            }}
                          />
                        </Box>
                        <Box>
                          <Typography variant="h6" sx={{ color: item.color, fontWeight: 700 }}>
                            {item.value}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {item.name}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box
              sx={{
                p: 3,
                bgcolor: 'white',
                borderRadius: 3,
                boxShadow: '0 0 20px rgba(0,0,0,0.05)'
              }}
            >
              <Typography variant="h6" sx={{ mb: 4, fontWeight: 600 }}>
                Lead Distribution Analysis
              </Typography>

              <Box sx={{ mt: 4 }}>
                {[...firstGridData, ...secondGridData, ...thirdGridData].map((item, index) => (
                  <Box
                    key={item.name}
                    sx={{
                      mb: 4,
                      '&:last-child': { mb: 0 }
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ color: 'text.primary', fontWeight: 600 }}>
                        {item.name}
                      </Typography>
                      <Typography variant="subtitle2" sx={{ color: item.color, fontWeight: 600 }}>
                        {item.value} ({item.percentage}%)
                      </Typography>
                    </Box>
                    <Box sx={{ position: 'relative', height: '24px' }}>
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          bgcolor: alpha(item.color, 0.1),
                          borderRadius: '12px'
                        }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: `${item.percentage}%`,
                          height: '100%',
                          background: item.gradient,
                          borderRadius: '12px',
                          transition: 'width 1s cubic-bezier(0.4, 0, 0.2, 1)',
                          animation: `${fadeIn} 1s ease-out`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          '&:hover': {
                            transform: 'scaleY(1.1)',
                            boxShadow: `0 4px 12px ${alpha(item.color, 0.3)}`
                          }
                        }}
                      >
                        <Box
                          sx={{
                            position: 'absolute',
                            right: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}
                        >
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: 'white',
                              opacity: 0.8
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        );

      case 2:
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={usersData}
              margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
              barSize={60}
            >
              <defs>
                {Object.keys(COLORS).map((color, index) => (
                  <linearGradient
                    key={color}
                    id={`barColor${index}`}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="0%"
                      stopColor={COLORS[color].main}
                      stopOpacity={1}
                    />
                    <stop
                      offset="100%"
                      stopColor={COLORS[color].light}
                      stopOpacity={1}
                    />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#E2E8F0"
                vertical={false}
              />
              <XAxis
                dataKey="name"
                stroke="#64748B"
                tick={{
                  fill: '#64748B',
                  fontSize: 12,
                  fontWeight: 500
                }}
                axisLine={{ stroke: '#E2E8F0' }}
                tickLine={{ stroke: '#E2E8F0' }}
              />
              <YAxis
                stroke="#64748B"
                tick={{
                  fill: '#64748B',
                  fontSize: 12,
                  fontWeight: 500
                }}
                axisLine={{ stroke: '#E2E8F0' }}
                tickLine={{ stroke: '#E2E8F0' }}
              />
              <Tooltip
                content={<CustomTooltip />}
                cursor={{ fill: 'rgba(255, 255, 255, 0.1)' }}
              />
              <Legend
                verticalAlign="top"
                height={36}
                iconType="circle"
                formatter={(value) => (
                  <span style={{
                    color: '#1E293B',
                    fontSize: '14px',
                    fontWeight: 600
                  }}>
                    {value}
                  </span>
                )}
              />
              <Bar
                dataKey="value"
                radius={[12, 12, 0, 0]}
                label={{
                  position: 'top',
                  fill: '#64748B',
                  fontSize: 12,
                  fontWeight: 600,
                  dy: -10
                }}
              >
                {usersData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={`url(#barColor${index})`}
                    style={{
                      filter: 'drop-shadow(0px 4px 6px rgba(0, 0, 0, 0.1))',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        );

      case 3:
        return (
          <ResponsiveContainer width="100%" height={400}>
            <PieChart>
              <defs>
                {Object.keys(COLORS).map((color, index) => (
                  <linearGradient
                    key={color}
                    id={`pieColor${index}`}
                    x1="0"
                    y1="0"
                    x2="1"
                    y2="1"
                  >
                    <stop
                      offset="0%"
                      stopColor={COLORS[color].main}
                      stopOpacity={1}
                    />
                    <stop
                      offset="100%"
                      stopColor={COLORS[color].light}
                      stopOpacity={1}
                    />
                  </linearGradient>
                ))}
              </defs>
              <Pie
                data={loansData}
                cx="50%"
                cy="50%"
                innerRadius={100}
                outerRadius={140}
                paddingAngle={5}
                dataKey="value"
                startAngle={90}
                endAngle={450}
              >
                {loansData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={`url(#pieColor${index})`}
                    stroke="none"
                    style={{
                      filter: 'drop-shadow(0px 4px 6px rgba(0, 0, 0, 0.1))',
                      cursor: 'pointer',
                      transition: 'transform 0.3s ease'
                    }}
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend
                verticalAlign="middle"
                align="right"
                layout="vertical"
                iconType="circle"
                formatter={(value, entry) => (
                  <span style={{
                    color: '#1E293B',
                    fontSize: '14px',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    {value}
                  </span>
                )}
                wrapperStyle={{
                  paddingLeft: '24px',
                  lineHeight: '2em'
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <DashboardContainer>
      <DashboardHeader>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={7}>
            <Box>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  color: '#1E293B',
                  mb: 1,
                  letterSpacing: '-1px',
                  background: COLORS.primary.gradient,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontSize: { xs: '1.75rem', md: '2.5rem' }
                }}
              >
                Analytics Dashboard
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: '#64748B',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  marginTop: '0.5rem',
                }}
              >
                <CalendarTodayIcon sx={{ fontSize: 20 }} />
                {new Date().toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </DashboardHeader>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={8.5}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Grid container spacing={3}>
            {quickStats.slice(0, 3).map((stat, index) => (
              <Grid item xs={12} sm={6} md={4} key={`first-row-${stat.title}`}>
                <GlassCard gradient={COLORS[stat.color].gradient} sx={{ minWidth: '230px' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
                      <Box
                        className="icon-container"
                        sx={{
                          width: 52,
                          height: 52,
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: COLORS[stat.color].gradient,
                          boxShadow: `0 8px 16px ${alpha(COLORS[stat.color].main, 0.2)}`
                        }}
                      >
                        {React.cloneElement(stat.icon, { sx: { fontSize: 26, color: 'white' } })} 
                      </Box>
                      {/* <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          bgcolor: stat.trend === 'up' ? COLORS.success.glass : COLORS.error.glass,
                          color: stat.trend === 'up' ? COLORS.success.main : COLORS.error.main,
                          px: 1.5,
                          py: 0.75,
                          borderRadius: 1.5,
                          fontSize: '0.875rem',
                          fontWeight: 600
                        }}
                      >
                        {stat.trend === 'up' ? <TrendingUpIcon sx={{ fontSize: 18, mr: 0.5 }} /> : <TrendingDownIcon sx={{ fontSize: 18, mr: 0.5 }} />}
                        {stat.percentage}%
                      </Box> */}
                    </Box>
                    <Typography variant="h4" sx={{ 
                      mb: 1,
                      fontWeight: 700,
                      fontSize: '2rem',
                      background: COLORS[stat.color].gradient,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}>
                      {stat.value}
                    </Typography>
                    <Typography variant="subtitle1" sx={{ color: '#64748B', fontWeight: 500, fontSize: '1rem' }}>  {/* Increased subtitle size */}
                      {stat.title}
                    </Typography>
                  </CardContent>
                </GlassCard>
              </Grid>
            ))}
          </Grid>

            <Grid container spacing={3}>
              {quickStats.slice(3, 6).map((stat, index) => (
                <Grid item xs={12} sm={6} md={4} key={`second-row-${stat.title}`}>
                  <GlassCard gradient={COLORS[stat.color].gradient} sx={{ minWidth: '230px' }}>  
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}> 
                        <Box
                          className="icon-container"
                          sx={{
                            width: 52,
                            height: 52,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: COLORS[stat.color].gradient,
                            boxShadow: `0 8px 16px ${alpha(COLORS[stat.color].main, 0.2)}`
                          }}
                        >
                          {React.cloneElement(stat.icon, { sx: { fontSize: 26, color: 'white' } })}
                        </Box>
                        {/* <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            bgcolor: stat.trend === 'up' ? COLORS.success.glass : COLORS.error.glass,
                            color: stat.trend === 'up' ? COLORS.success.main : COLORS.error.main,
                            px: 1.5,
                            py: 0.75,
                            borderRadius: 1.5,
                            fontSize: '0.875rem',
                            fontWeight: 600
                          }}
                        >
                          {stat.trend === 'up' ? <TrendingUpIcon sx={{ fontSize: 18, mr: 0.5 }} /> : <TrendingDownIcon sx={{ fontSize: 18, mr: 0.5 }} />}
                          {stat.percentage}%
                        </Box> */}
                      </Box>
                      <Typography variant="h4" sx={{
                        mb: 1,
                        fontWeight: 700,
                        fontSize: '2rem',
                        background: COLORS[stat.color].gradient,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                      }}>
                        {stat.value}
                      </Typography>
                      <Typography variant="subtitle1" sx={{ color: '#64748B', fontWeight: 500, fontSize: '1rem' }}>  {/* Increased subtitle size */}
                        {stat.title}
                      </Typography>
                    </CardContent>
                  </GlassCard>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box sx={{ mt: 4 }}>
            <GlassCard gradient={COLORS.primary.gradient}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ px: 4, pt: 4, pb: 3 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      color: '#1E293B',
                      fontWeight: 700,
                      mb: 3,
                      letterSpacing: '-0.02em',
                      fontSize: '1.5rem',
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: '-8px',
                        left: 0,
                        width: '40px',
                        height: '3px',
                        borderRadius: '1.5px',
                        background: COLORS.primary.modernGradient
                      }
                    }}
                  >
                    Analytics Overview
                  </Typography>
                  <Tabs
                    value={selectedChart}
                    onChange={(e, newValue) => setSelectedChart(newValue)}
                    variant="standard"
                    sx={{
                      mb: 2,
                      '& .MuiTabs-root': {
                        minHeight: 'auto'
                      },
                      '& .MuiTab-root': {
                        minWidth: 'auto',
                        padding: '12px 24px',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '1rem',
                        color: '#64748B',
                        borderRadius: '12px',
                        mx: 0.5,
                        transition: 'all 0.3s ease',
                        '&.Mui-selected': {
                          color: COLORS.primary.main,
                          bgcolor: COLORS.primary.glass,
                          boxShadow: `0 4px 8px ${alpha(COLORS.primary.main, 0.1)}`,
                        },
                        '&:hover': {
                          bgcolor: alpha(COLORS.primary.main, 0.05),
                          color: COLORS.primary.main,
                          transform: 'translateY(-2px)'
                        }
                      },
                      '& .MuiTabs-indicator': {
                        display: 'none'
                      },
                    }}
                  >
                    <Tab label="Overview" />
                    <Tab label="Leads Analysis" />
                    <Tab label="Users Distribution" />
                    <Tab label="Loans Status" />
                  </Tabs>
                </Box>

                <Divider sx={{ opacity: 0.5 }} />

                <Box sx={{ p: 4 }}>
                  {renderChart()}
                </Box>
              </CardContent>
            </GlassCard>
          </Box>
        </Grid>

        <Grid item xs={12} lg={3}>
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
            <GlassCard gradient={COLORS.warning.gradient}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography
                    variant="h6"
                    className="card-header"
                    sx={{
                      fontWeight: 700,
                      fontSize: '1.25rem',
                      letterSpacing: '-0.02em',
                      color: '#1E293B',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    Latest Offers
                  </Typography>
                  <ModernButton
                    endIcon={<ArrowForwardIcon />}
                    color="warning"
                    variant="text"
                    onClick={handleViewAllOffers}
                    sx={{
                      color: COLORS.warning.main,
                      background: 'none',
                      boxShadow: 'none',
                      '&:hover': {
                        background: COLORS.warning.glass,
                        transform: 'translateX(4px)',
                        boxShadow: 'none'
                      }
                    }}
                  >
                    View All
                  </ModernButton>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {offers.map((offer, index) => (
                    <Box
                      key={offer.id}
                      sx={{
                        p: 2.5,
                        bgcolor: alpha(COLORS.warning.main, 0.05),
                        borderRadius: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        border: `1px solid ${alpha(COLORS.warning.main, 0.1)}`,
                        '&:hover': {
                          transform: 'translateX(8px) scale(1.02)',
                          bgcolor: alpha(COLORS.warning.main, 0.1),
                          boxShadow: `0 8px 16px ${alpha(COLORS.warning.main, 0.15)}`,
                          borderColor: alpha(COLORS.warning.main, 0.2)
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                          src={offer.bankLogo || ''}
                          sx={{
                            width: 48,
                            height: 48,
                            bgcolor: 'white',
                            p: 1,
                            boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                            border: '2px solid rgba(255,255,255,0.8)'
                          }}
                        >
                          {!offer.bankLogo && offer.bankName?.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 600,
                              color: '#1E293B',
                              fontSize: '1rem',
                              mb: 0.5
                            }}
                          >
                            {offer.bankName}
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#64748B',
                                fontSize: '0.875rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                              }}
                            >
                              <span style={{ color: COLORS.warning.main }}>{offer.interestRate}%</span>
                              •
                              <span>{offer.tenure} Years</span>
                            </Typography>
                            <Chip
                              label={offer.status}
                              size="small"
                              sx={{
                                ml: 1,
                                textTransform: 'capitalize',
                                bgcolor: offer.status === 'accepted'
                                  ? COLORS.success.glass
                                  : offer.status === 'rejected'
                                    ? COLORS.error.glass
                                    : COLORS.warning.glass,
                                color: offer.status === 'accepted'
                                  ? COLORS.success.main
                                  : offer.status === 'rejected'
                                    ? COLORS.error.main
                                    : COLORS.warning.main,
                                fontWeight: 600,
                                fontSize: '0.75rem'
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 700,
                          background: COLORS.warning.modernGradient,
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          fontSize: '1.1rem'
                        }}
                      >
                        ₹{offer.amount} Lakhs
                      </Typography>
                    </Box>
                  ))}
                  {offers.length === 0 && (
                    <Box sx={{
                      p: 3,
                      textAlign: 'center',
                      color: '#64748B',
                      bgcolor: alpha(COLORS.warning.main, 0.05),
                      borderRadius: '16px',
                    }}>
                      <Typography>No offers available</Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </GlassCard>

            <GlassCard gradient={COLORS.success.gradient}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography
                    variant="h6"
                    className="card-header"
                    sx={{
                      fontWeight: 700,
                      fontSize: '1.25rem',
                      letterSpacing: '-0.02em',
                      color: '#1E293B',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    Recent Loans
                  </Typography>
                  <ModernButton
                    endIcon={<ArrowForwardIcon />}
                    color="success"
                    variant="text"
                    onClick={handleViewAllLoans}
                    sx={{
                      color: COLORS.success.main,
                      background: 'none',
                      boxShadow: 'none',
                      '&:hover': {
                        background: COLORS.success.glass,
                        transform: 'translateX(4px)',
                        boxShadow: 'none'
                      }
                    }}
                  >
                    View All
                  </ModernButton>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {recentLoans.map((loan) => (
                    <Box
                      key={loan.id}
                      sx={{
                        p: 2,
                        bgcolor: alpha(COLORS.success.main, 0.05),
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        border: `1px solid ${alpha(COLORS.success.main, 0.1)}`,
                        '&:hover': {
                          transform: 'translateX(8px)',
                          bgcolor: alpha(COLORS.success.main, 0.1),
                          boxShadow: `0 4px 12px ${alpha(COLORS.success.main, 0.15)}`,
                          borderColor: alpha(COLORS.success.main, 0.2)
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                          sx={{
                            bgcolor: COLORS.success.main,
                            boxShadow: `0 4px 8px ${alpha(COLORS.success.main, 0.2)}`
                          }}
                        >
                          {loan.borrowerName?.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1E293B', mb: 0.5 }}>
                            {loan.applicationNumber}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" sx={{ color: '#64748B' }}>
                              {loan.borrowerName}
                            </Typography>
                            <Chip
                              label={loan.status}
                              size="small"
                              sx={{
                                textTransform: 'capitalize',
                                bgcolor: loan.status === 'approved'
                                  ? COLORS.success.glass
                                  : loan.status === 'rejected'
                                    ? COLORS.error.glass
                                    : COLORS.warning.glass,
                                color: loan.status === 'approved'
                                  ? COLORS.success.main
                                  : loan.status === 'rejected'
                                    ? COLORS.error.main
                                    : COLORS.warning.main,
                                fontWeight: 600,
                                fontSize: '0.75rem'
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          background: COLORS.success.gradient,
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent'
                        }}
                      >
                        ₹{loan.amount} Lakhs
                      </Typography>
                    </Box>
                  ))}
                  {recentLoans.length === 0 && (
                    <Box sx={{
                      p: 3,
                      textAlign: 'center',
                      color: '#64748B',
                      bgcolor: alpha(COLORS.success.main, 0.05),
                      borderRadius: '16px',
                    }}>
                      <Typography>No recent loans</Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </GlassCard>
          </Box>
        </Grid>
      </Grid>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 1,
            overflow: 'visible',
            filter: 'drop-shadow(0px 4px 12px rgba(0,0,0,0.1))',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: '16px',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            '& .MuiMenuItem-root': {
              fontSize: '0.875rem',
              fontWeight: 500,
              py: 1.5,
              px: 2,
              transition: 'all 0.3s ease',
              borderRadius: '8px',
              margin: '4px',
              '&:hover': {
                bgcolor: COLORS.primary.glass,
                color: COLORS.primary.main,
                transform: 'translateX(4px)'
              }
            }
          }
        }}
      >
        <MenuItem onClick={handleMenuClose}>View Details</MenuItem>
        <MenuItem onClick={handleMenuClose}>Download Report</MenuItem>
        <MenuItem onClick={handleMenuClose}>Share</MenuItem>
      </Menu>
    </DashboardContainer>
  );
};

export default Dashboard;